import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const benefits = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: '$500K Clinical Compensation',
    desc: 'A guaranteed $500,000 annual pay package covering your full clinical role — so you can focus entirely on patient care without the administrative burden.',
    color: '#059669',
  },
  {
    icon: 'ri-building-2-line',
    title: '100% Practice Ownership',
    desc: 'You retain complete, unencumbered ownership of your medical practice. Your patients, your brand, your legacy — nothing changes on that front.',
    color: '#1B4B7F',
  },
  {
    icon: 'ri-pie-chart-2-line',
    title: '40% MSO Equity Stake',
    desc: 'Gain a 40% ownership interest in MedxMSO itself — a growing management services organization with a portfolio of practices, revenue streams, and enterprise value.',
    color: '#D97706',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Unlimited Growth Upside',
    desc: 'As MedxMSO scales — adding more practices, services, and revenue — your 40% stake appreciates. You\'re not just a client, you\'re a co-owner of the platform.',
    color: '#0D9488',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Zero Admin Headaches',
    desc: 'MedxMSO handles 100% of the operational, billing, HR, compliance, and financial management. You show up, see patients, and go home.',
    color: '#7C3AED',
  },
  {
    icon: 'ri-team-line',
    title: 'Strategic Board Seat',
    desc: 'As a 40% equity partner, you have a meaningful voice in MedxMSO\'s strategic direction — shaping the organization you\'re building together.',
    color: '#0891B2',
  },
];

const comparison = [
  { label: 'Practice Ownership', vendor: 'Retained 100%', partner: 'Retained 100%', both: true },
  { label: 'Clinical Autonomy', vendor: 'Full', partner: 'Full', both: true },
  { label: 'Admin Burden', vendor: 'Reduced via MSO', partner: 'Eliminated', both: false },
  { label: 'Compensation Model', vendor: 'Keep all collections minus fees', partner: '$500K guaranteed package', both: false },
  { label: 'Ops Fee (2.5%)', vendor: 'Yes', partner: 'No', both: false },
  { label: 'RCM Fee (2.5%)', vendor: 'Optional add-on', partner: 'No', both: false },
  { label: 'MSO Equity', vendor: 'None', partner: '40% ownership', both: false },
  { label: 'Growth Upside', vendor: 'Practice revenue only', partner: 'Practice + MSO enterprise value', both: false },
  { label: 'Onboarding Fee', vendor: '$2,500', partner: 'Waived', both: false },
  { label: 'Monthly Base Fee', vendor: '$250/provider', partner: 'Waived', both: false },
];

const faqs = [
  {
    q: 'Do I give up my medical license or practice ownership?',
    a: 'Absolutely not. You retain 100% ownership of your medical practice and your license remains entirely yours. The partnership is a business arrangement — your clinical entity is untouched.',
  },
  {
    q: 'How is the $500K compensation structured?',
    a: 'The $500,000 annual package is structured as a clinical services agreement — compensating you for your time, expertise, and patient care activities. It is paid on a regular schedule (monthly or bi-weekly) and is separate from any equity distributions.',
  },
  {
    q: 'What does 40% equity in MedxMSO actually mean?',
    a: 'You receive a 40% ownership stake in MedxMSO as a company. This means you share in 40% of the MSO\'s profits, enterprise value, and any future liquidity events (acquisitions, expansions, etc.). As MedxMSO grows its portfolio of practices and services, your stake grows in value.',
  },
  {
    q: 'Can I still see my existing patients?',
    a: 'Yes — nothing changes about your patient relationships. You continue practicing medicine exactly as you do today. The only difference is that MedxMSO handles all the business operations behind the scenes.',
  },
  {
    q: 'What happens if I want to exit the partnership?',
    a: 'Partnership agreements include clearly defined exit provisions. We work with each partner to structure terms that protect both parties, including buyout provisions, transition timelines, and equity valuation methodologies.',
  },
  {
    q: 'Is this model available for group practices?',
    a: 'The Partnership Model is currently designed for single-practice physicians. For group practices or multi-provider organizations, our MSO Vendor Model may be a better fit. Contact us to discuss your specific situation.',
  },
];

export default function PartnershipPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-gradient-to-b from-[#0A1628] to-[#1B4B7F]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/50f4002bde5ecf02750e3107d0496ef3/6f24c25eed393f63407e0e21645fee7d.png"
                alt="MedxMSO Logo"
                className="h-12 w-auto"
              />
              <span className={`text-xl font-black tracking-tight whitespace-nowrap ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>MedxMSO</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Home</Link>
              <Link to="/services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Services</Link>
              <Link to="/pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Models</Link>
              <Link to="/mso-pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Vendor Pricing</Link>
              <Link to="/partnership" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#059669]' : 'text-[#6EE7B7]'}`}>Partnership</Link>
              <Link to="/why-mso" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Why MSO?</Link>
              <Link to="/audit" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#BE185D] hover:text-[#9D174D]' : 'text-[#F9A8D4] hover:text-white'}`}>Request Audit</Link>
              <Link to="/#contact" className={`px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'bg-[#059669] text-white hover:bg-[#047857]' : 'bg-white/20 border border-white/40 text-white hover:bg-white/30'}`}>
                Explore Partnership
              </Link>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <Link to="/" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Home</Link>
              <Link to="/services" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Services</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Models</Link>
              <Link to="/mso-pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Vendor Pricing</Link>
              <Link to="/partnership" className="block text-sm font-semibold text-[#059669] cursor-pointer">Partnership</Link>
              <Link to="/why-mso" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Why MSO?</Link>
              <Link to="/audit" className="block text-sm font-semibold text-[#BE185D] cursor-pointer">Request Audit</Link>
              <Link to="/#contact" className="block bg-[#059669] text-white px-6 py-2.5 rounded-full text-sm font-medium text-center cursor-pointer">Explore Partnership</Link>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9b6af8c44f6eab5c007c9a76ba87cfc7.jpg"
            alt="MedxMSO Partnership"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/85 to-[#0A1628]/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-40 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#059669]/20 border border-[#059669]/40 text-[#6EE7B7] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-8">
              <i className="ri-vip-crown-line text-base"></i>
              PARTNERSHIP MODEL — EXCLUSIVE OPPORTUNITY
            </div>
            <h1 className="text-6xl lg:text-8xl font-black text-white leading-none mb-6">
              Don't Just Use<br />
              Our MSO.<br />
              <span className="text-[#6EE7B7]">Own Part of It.</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
              A select partnership model for single-practice physicians who want to eliminate admin entirely, earn a guaranteed $500K clinical package, and gain 40% equity in a growing healthcare management organization.
            </p>
            <div className="flex flex-wrap gap-4 mb-12">
              <a href="#partnership-details" className="bg-[#059669] text-white px-10 py-4 rounded-full text-sm font-bold hover:bg-[#047857] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                See How It Works
                <i className="ri-arrow-down-line"></i>
              </a>
              <Link to="/#contact" className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full text-sm font-bold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
                Schedule a Conversation
              </Link>
            </div>
            {/* Key Numbers */}
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-4xl font-black text-[#6EE7B7]">$500K</div>
                <div className="text-sm text-gray-400 mt-1">Annual clinical package</div>
              </div>
              <div className="w-px bg-white/20"></div>
              <div>
                <div className="text-4xl font-black text-[#6EE7B7]">40%</div>
                <div className="text-sm text-gray-400 mt-1">MSO equity ownership</div>
              </div>
              <div className="w-px bg-white/20"></div>
              <div>
                <div className="text-4xl font-black text-[#6EE7B7]">100%</div>
                <div className="text-sm text-gray-400 mt-1">Practice ownership retained</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CONCEPT */}
      <section id="partnership-details" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-6 font-semibold">THE PARTNERSHIP CONCEPT</p>
              <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-8">
                You Focus on<br />
                Medicine. We Run<br />
                <span className="text-[#059669]">Everything Else.</span>
              </h2>
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                <p>
                  Most physicians spend 30–40% of their time on administrative tasks that have nothing to do with patient care. That's not just inefficient — it's a waste of your most valuable asset: your clinical expertise.
                </p>
                <p>
                  The MedxMSO Partnership Model is built for the physician who wants to practice medicine at the highest level, earn a premium compensation package, and simultaneously build long-term wealth through equity ownership in a growing MSO.
                </p>
                <p>
                  You keep your practice. You keep your patients. You keep your autonomy. You just stop doing the parts you never wanted to do in the first place.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[500px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Confident%20physician%20in%20white%20coat%20standing%20in%20modern%20medical%20office%20looking%20relaxed%20and%20satisfied%20with%20arms%20crossed%20representing%20doctor%20who%20has%20delegated%20administrative%20work%20and%20focuses%20purely%20on%20patient%20care%20in%20bright%20contemporary%20healthcare%20environment%20with%20clean%20professional%20atmosphere&width=800&height=900&seq=partnership-doctor-focus&orientation=portrait"
                  alt="Physician focused on care"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#059669] text-white rounded-2xl p-6 shadow-xl">
                <div className="text-3xl font-black mb-1">0%</div>
                <div className="text-sm font-medium text-green-100">Admin fees on collections</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="py-28 bg-[#F0FDF4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-4 font-semibold">WHAT YOU GET</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Everything in the Partnership</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Six core pillars that define what it means to be a MedxMSO partner — not just a client.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${b.color}15` }}>
                  <i className={`${b.icon} text-2xl`} style={{ color: b.color }}></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-3">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE NUMBERS — VISUAL BREAKDOWN */}
      <section className="py-28 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#6EE7B7] mb-4 font-semibold">THE ECONOMICS</p>
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
              What the Numbers<br />
              <span className="text-[#6EE7B7]">Actually Look Like</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">A single-practice physician in the Partnership Model — here's the full financial picture.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {/* Card 1 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 bg-[#059669]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-stethoscope-line text-[#6EE7B7] text-3xl"></i>
              </div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Clinical Compensation</div>
              <div className="text-6xl font-black text-[#6EE7B7] mb-2">$500K</div>
              <div className="text-gray-400 text-sm leading-relaxed">Annual guaranteed pay package for your clinical role — paid on a regular schedule, regardless of collections fluctuations.</div>
            </div>

            {/* Card 2 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 bg-[#D97706]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-2-line text-[#FCD34D] text-3xl"></i>
              </div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">MSO Equity Stake</div>
              <div className="text-6xl font-black text-[#FCD34D] mb-2">40%</div>
              <div className="text-gray-400 text-sm leading-relaxed">Ownership in MedxMSO — a growing MSO with multiple revenue streams, practice clients, and compounding enterprise value.</div>
            </div>

            {/* Card 3 */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-10 text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 bg-[#1B4B7F]/40 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hospital-line text-[#93C5FD] text-3xl"></i>
              </div>
              <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Practice Ownership</div>
              <div className="text-6xl font-black text-[#93C5FD] mb-2">100%</div>
              <div className="text-gray-400 text-sm leading-relaxed">Your practice remains entirely yours. Your patients, your brand, your license — completely unaffected by the partnership structure.</div>
            </div>
          </div>

          {/* What you DON'T pay */}
          <div className="bg-white/5 border border-[#059669]/30 rounded-3xl p-10">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-black text-white mb-2">What You Don't Pay in the Partnership Model</h3>
              <p className="text-gray-400 text-sm">Unlike the Vendor Model, partners have zero revenue-based fees</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { label: 'Ops Revenue Share', amount: '0%', note: 'vs. 2.5% in Vendor Model', icon: 'ri-line-chart-line' },
                { label: 'RCM Revenue Share', amount: '0%', note: 'vs. 2.5% add-on in Vendor Model', icon: 'ri-money-dollar-circle-line' },
                { label: 'Onboarding Fee', amount: '$0', note: 'vs. $2,500 in Vendor Model', icon: 'ri-rocket-line' },
              ].map((item, i) => (
                <div key={i} className="bg-[#059669]/10 border border-[#059669]/20 rounded-2xl p-6 text-center">
                  <div className="w-10 h-10 bg-[#059669]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-[#6EE7B7] text-xl`}></i>
                  </div>
                  <div className="text-3xl font-black text-[#6EE7B7] mb-1">{item.amount}</div>
                  <div className="text-white font-semibold text-sm mb-1">{item.label}</div>
                  <div className="text-gray-500 text-xs">{item.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-4 font-semibold">SIDE BY SIDE</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Vendor vs. Partner — Which Path Is Right for You?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Both models protect your clinical independence. The difference is in how you want to structure the business relationship.</p>
          </div>

          <div className="rounded-3xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="grid grid-cols-3 bg-[#0A1628]">
              <div className="p-6 text-gray-400 text-sm font-semibold">Feature</div>
              <div className="p-6 text-center border-l border-white/10">
                <div className="text-white font-black text-base mb-1">MSO Vendor Model</div>
                <div className="text-gray-400 text-xs">You hire MedxMSO as your vendor</div>
              </div>
              <div className="p-6 text-center border-l border-white/10 bg-[#059669]/20">
                <div className="text-[#6EE7B7] font-black text-base mb-1">Partnership Model</div>
                <div className="text-green-300 text-xs">You become a co-owner of MedxMSO</div>
              </div>
            </div>

            {/* Rows */}
            {comparison.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}`}>
                <div className="p-5 text-sm font-semibold text-gray-700">{row.label}</div>
                <div className="p-5 text-center border-l border-gray-100">
                  <span className={`text-sm font-medium ${row.vendor === 'No' || row.vendor === 'None' ? 'text-gray-400' : 'text-gray-800'}`}>{row.vendor}</span>
                </div>
                <div className="p-5 text-center border-l border-gray-100 bg-[#F0FDF4]">
                  <span className={`text-sm font-bold ${row.partner === 'No' || row.partner === 'None' ? 'text-gray-400' : 'text-[#059669]'}`}>{row.partner}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing" className="bg-[#1B4B7F] text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-[#153a63] transition-all whitespace-nowrap cursor-pointer text-center">
              View Vendor Model Pricing
            </Link>
            <Link to="/#contact" className="bg-[#059669] text-white px-8 py-4 rounded-full text-sm font-bold hover:bg-[#047857] transition-all whitespace-nowrap cursor-pointer text-center">
              Explore Partnership
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-28 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-4 font-semibold">THE PROCESS</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">How the Partnership Comes Together</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">A structured, transparent process from first conversation to full partnership activation.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', icon: 'ri-chat-3-line', title: 'Discovery Call', desc: 'A confidential conversation to understand your practice, goals, and whether the Partnership Model is the right fit for your situation.' },
              { step: '02', icon: 'ri-file-list-3-line', title: 'Term Sheet', desc: 'We present a clear, plain-language term sheet outlining the $500K package, 40% equity structure, and all partnership terms for your review.' },
              { step: '03', icon: 'ri-scales-3-line', title: 'Legal & Due Diligence', desc: 'Both parties conduct due diligence. We encourage you to involve your attorney. Full transparency on MedxMSO financials and structure.' },
              { step: '04', icon: 'ri-rocket-line', title: 'Partnership Activation', desc: 'Agreements signed, equity transferred, and MedxMSO takes over all administrative operations. You focus on patients from day one.' },
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="text-6xl font-black text-gray-100 leading-none mb-4">{step.step}</div>
                <div className="w-12 h-12 bg-[#059669]/10 rounded-2xl flex items-center justify-center mb-5">
                  <i className={`${step.icon} text-[#059669] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="relative rounded-3xl overflow-hidden h-[500px]">
              <img
                src="https://readdy.ai/api/search-image?query=Successful%20senior%20physician%20in%20modern%20medical%20office%20reviewing%20documents%20with%20satisfied%20expression%20representing%20experienced%20doctor%20considering%20strategic%20business%20partnership%20opportunity%20with%20healthcare%20management%20organization%20in%20bright%20professional%20contemporary%20setting&width=800&height=900&seq=partnership-ideal-doctor&orientation=portrait"
                alt="Ideal Partnership Candidate"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-6 font-semibold">IS THIS FOR YOU?</p>
              <h2 className="text-5xl font-black text-gray-900 leading-tight mb-8">
                The Partnership Model<br />
                Is Designed For...
              </h2>
              <div className="space-y-5">
                {[
                  { icon: 'ri-check-double-line', text: 'Single-practice physicians who are tired of wearing too many hats' },
                  { icon: 'ri-check-double-line', text: 'Doctors who want guaranteed income stability without collections risk' },
                  { icon: 'ri-check-double-line', text: 'Physicians who want to build long-term wealth beyond their practice' },
                  { icon: 'ri-check-double-line', text: 'Providers who value clinical autonomy but want business support' },
                  { icon: 'ri-check-double-line', text: 'Doctors approaching a transition point — retirement, expansion, or burnout recovery' },
                  { icon: 'ri-check-double-line', text: 'Physicians who want a seat at the table in a growing healthcare organization' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#059669]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#059669] text-sm`}></i>
                    </div>
                    <p className="text-gray-700 text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 bg-[#FEF3C7] rounded-2xl border border-[#FCD34D]/40">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-[#D97706] text-xl mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>Currently available for single-practice physicians only.</strong> Multi-provider groups and specialty centers should explore our MSO Vendor Model for tailored enterprise solutions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 bg-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[3px] text-[#059669] mb-4 font-semibold">COMMON QUESTIONS</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Straight answers to the questions physicians ask most.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-7 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-base font-bold text-gray-900 pr-4">{faq.q}</span>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[#059669] text-xl`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-7 pb-7">
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20modern%20city%20skyline%20at%20golden%20hour%20representing%20growth%20opportunity%20and%20business%20success%20with%20warm%20golden%20light%20reflecting%20off%20glass%20buildings%20representing%20healthcare%20business%20partnership%20and%20future%20growth%20potential&width=1920&height=900&seq=partnership-cta-bg&orientation=landscape"
            alt="Partnership Opportunity"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/97 via-[#0A1628]/90 to-[#0A1628]/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[3px] text-[#6EE7B7] mb-6 font-semibold">READY TO EXPLORE?</p>
            <h2 className="text-6xl lg:text-7xl font-black text-white leading-none mb-8">
              Let's Have a<br />
              Confidential<br />
              <span className="text-[#6EE7B7]">Conversation.</span>
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
              No pressure, no commitment. Just a candid discussion about whether the MedxMSO Partnership Model aligns with where you want to take your career and your practice.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/#contact" className="bg-[#059669] text-white px-10 py-5 rounded-full text-base font-bold hover:bg-[#047857] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer">
                Schedule a Confidential Call
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <Link to="/pricing" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-base font-bold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
                Compare Both Models
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-8">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-lock-line text-[#6EE7B7]"></i>
                100% Confidential
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-time-line text-[#6EE7B7]"></i>
                30-Minute Discovery Call
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-close-circle-line text-[#6EE7B7]"></i>
                No Obligation
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[#1A2942] text-[180px] font-bold opacity-50 leading-none select-none pointer-events-none">
          MEDXMSO
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-white text-4xl font-bold mb-3">MedxMSO</div>
              <p className="text-gray-400 text-sm mb-16">Healthcare Management Excellence</p>
              <div className="text-white text-lg leading-relaxed">
                <p>Empowering providers</p>
                <p className="text-gray-400">since 2015</p>
              </div>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Our Models</h4>
              <ul className="space-y-4">
                <li><Link to="/services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">MSO Vendor Services</Link></li>
                <li><Link to="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Vendor Pricing</Link></li>
                <li><Link to="/partnership" className="text-[#6EE7B7] text-sm hover:text-white transition-colors cursor-pointer">Partnership Model</Link></li>
                <li><Link to="/why-mso" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Why MSO?</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="text-gray-400 text-sm">(939) 399-2059</li>
                <li className="text-gray-400 text-sm">info@medxmso.com</li>
                <li className="text-gray-400 text-sm">Mon-Fri 6am-6pm EST</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Connect</h4>
              <ul className="space-y-4">
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-linkedin-line text-xl"></i>LinkedIn</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-twitter-x-line text-xl"></i>Twitter</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-facebook-line text-xl"></i>Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2024 MedxMSO. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
